/**
 * RippleBufferPass.ts — boat-wake wave equation, ping-pong rg32float.
 *
 * State layout per cell:
 *   .r = surface elevation h (m)
 *   .g = vertical velocity h_t (m/s)
 *
 * Texture is anchored to a fixed world centre that snaps to the player boat
 * each frame in cell-aligned increments. This allows the wake to follow the
 * camera while keeping the underlying grid stationary on a per-step basis;
 * acceptable because the ripple lifetime (~3 s) is short compared to the
 * boat's traversal of half the texture (~10 s @ 10 m/s for 200 m extent).
 *
 * Sampled by the water shader at binding 17.
 */

import wgsl from "../shaders/ripple.wgsl?raw";
import type { TerrainRenderPass } from "./TerrainRenderPass";
import { createUniformBuffer } from "../gpu/Buffers";
import type { Boat } from "../sim/Boat";
import type { FrameContext, RenderPass } from "../rendering/RenderPass";

const MAX_BOATS = 8;
// 2 + 1 + 1 + 1 + 1 + 1 + 1 + 1 + 1 + 1 + 1 + 1 = 13 floats header, padded to 16.
// header: centerXZ(2), extent, res, dt, c2, gamma, sigma, sceneExtent, waterLevel, boatCount, pad
// = 12 floats + boats(MAX_BOATS × 4 floats)
const HEADER_FLOATS = 12;
const BOAT_FLOATS = 4;
const STRUCT_FLOATS = HEADER_FLOATS + MAX_BOATS * BOAT_FLOATS;
const STRUCT_SIZE = STRUCT_FLOATS * 4;

export interface RipplePassOptions {
  resolution?: number;
  /** World-space size covered by the ripple texture (m). Default 200. */
  extent?: number;
  /** Wave speed (m/s). Default 3.5. */
  c?: number;
  /** Damping (1/s). Default 0.45. */
  gamma?: number;
  /** Source gaussian sigma (m). Default 0.8. */
  sigma?: number;
}

export class RippleBufferPass implements RenderPass {
  readonly name = "RippleBufferPass";

  readonly resolution: number;
  readonly extent: number;
  readonly c: number;
  readonly gamma: number;
  readonly sigma: number;

  private device: GPUDevice;
  private terrainPass: TerrainRenderPass;
  private boats: Boat[];
  private pipeline: GPUComputePipeline;
  private bgl: GPUBindGroupLayout;
  private uniform: GPUBuffer;
  private texA: GPUTexture;
  private texB: GPUTexture;
  private viewA: GPUTextureView;
  private viewB: GPUTextureView;
  private bgAB: GPUBindGroup;
  private bgBA: GPUBindGroup;
  private currentIsB = true;
  /** World-XZ centre of the ripple texture (cell-snapped per frame). */
  readonly centerXZ: [number, number] = [0, 0];
  private prevSpeed = new Map<Boat, number>();

  private cpu = new ArrayBuffer(STRUCT_SIZE);
  private f32 = new Float32Array(this.cpu);
  private u32 = new Uint32Array(this.cpu);

  constructor(
    device: GPUDevice,
    terrainPass: TerrainRenderPass,
    boats: Boat[],
    opts: RipplePassOptions = {},
  ) {
    this.device = device;
    this.terrainPass = terrainPass;
    this.boats = boats;
    this.resolution = opts.resolution ?? 512;
    this.extent = opts.extent ?? 200;
    // Wave speed. Linear (non-dispersive) wave equation produces a Mach cone
    // with half-angle = asin(c/v). To mimic Kelvin's 19.47° wedge at a
    // typical lake-boat speed of ~3.3 m/s we need c/v ≈ tan(19.47°) ≈ 0.35
    // → c ≈ 1.2 m/s. At slower speeds (v < c) the cone collapses to
    // expanding circles (the boat is "subsonic" w.r.t. surface waves),
    // which is also physically correct.
    this.c = opts.c ?? 1.2;
    // Damping (1/s). Lower γ so trailing wavelets travel far enough to form
    // the V envelope before fading; γ=0.25 → amplitude halves in ~2.8 s,
    // dies in ~12 s. Combined with the 10 cm h clamp this avoids the
    // texture saturating.
    this.gamma = opts.gamma ?? 0.25;
    // Source Gaussian sigma. Narrower than the boat beam so each frame's
    // impulse acts like a point source — adjacent frames' wavelets then
    // constructively interfere along the Mach angle into a sharp V. Wider
    // sources smear the wedge into a diffuse hemisphere of circles.
    this.sigma = opts.sigma ?? 0.6;

    const usage =
      GPUTextureUsage.STORAGE_BINDING |
      GPUTextureUsage.TEXTURE_BINDING |
      GPUTextureUsage.COPY_DST;
    this.texA = device.createTexture({
      label: "RippleBufferPass.A",
      size: { width: this.resolution, height: this.resolution },
      format: "rg32float",
      usage,
    });
    this.texB = device.createTexture({
      label: "RippleBufferPass.B",
      size: { width: this.resolution, height: this.resolution },
      format: "rg32float",
      usage,
    });
    this.viewA = this.texA.createView({ label: "RippleBufferPass.A.view" });
    this.viewB = this.texB.createView({ label: "RippleBufferPass.B.view" });

    // Zero-initialise both textures.
    const zeros = new Float32Array(this.resolution * this.resolution * 2);
    for (const tex of [this.texA, this.texB]) {
      device.queue.writeTexture(
        { texture: tex },
        zeros.buffer,
        { bytesPerRow: this.resolution * 8, rowsPerImage: this.resolution },
        { width: this.resolution, height: this.resolution },
      );
    }

    this.uniform = createUniformBuffer(
      device,
      STRUCT_SIZE,
      "RippleBufferPass.params",
    );

    const module = device.createShaderModule({
      label: "ripple.wgsl",
      code: wgsl,
    });
    this.bgl = device.createBindGroupLayout({
      label: "RippleBufferPass.bgl",
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.COMPUTE,
          buffer: { type: "uniform" },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.COMPUTE,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.COMPUTE,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 3,
          visibility: GPUShaderStage.COMPUTE,
          storageTexture: { access: "write-only", format: "rg32float" },
        },
      ],
    });
    this.pipeline = device.createComputePipeline({
      label: "RippleBufferPass.pipeline",
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.bgl] }),
      compute: { module, entryPoint: "cs_main" },
    });
    this.bgAB = this.makeBindGroup(this.viewA, this.viewB, "AB");
    this.bgBA = this.makeBindGroup(this.viewB, this.viewA, "BA");
  }

  private makeBindGroup(
    inView: GPUTextureView,
    outView: GPUTextureView,
    label: string,
  ): GPUBindGroup {
    return this.device.createBindGroup({
      label: `RippleBufferPass.bg.${label}`,
      layout: this.bgl,
      entries: [
        { binding: 0, resource: { buffer: this.uniform } },
        { binding: 1, resource: this.terrainPass.heightmapView() },
        { binding: 2, resource: inView },
        { binding: 3, resource: outView },
      ],
    });
  }

  /** Currently-most-recently-written view (for water shader binding). */
  get currentView(): GPUTextureView {
    return this.currentIsB ? this.viewB : this.viewA;
  }

  /** Both views, A first then B; consumers pick by `isViewB`. */
  views(): { a: GPUTextureView; b: GPUTextureView } {
    return { a: this.viewA, b: this.viewB };
  }

  get currentIsViewB(): boolean {
    return this.currentIsB;
  }

  encode(encoder: GPUCommandEncoder, frame: FrameContext): void {
    // Snap-centre on the first boat (player). Cell-aligned increments avoid
    // sub-cell aliasing of the ripple field while keeping the source on the
    // boat regardless of how far it has travelled in world space.
    const player = this.boats[0];
    if (player) {
      const cellSize = this.extent / this.resolution;
      this.centerXZ[0] = Math.round(player.posX / cellSize) * cellSize;
      this.centerXZ[1] = Math.round(player.posZ / cellSize) * cellSize;
    }

    const dt = Math.min(frame.dt, 1 / 60);
    const f = this.f32;
    f[0] = this.centerXZ[0];
    f[1] = this.centerXZ[1];
    f[2] = this.extent;
    f[3] = this.resolution;
    f[4] = dt;
    f[5] = this.c * this.c;
    f[6] = this.gamma;
    f[7] = this.sigma;
    f[8] = this.terrainPass.heightmap.extent;
    f[9] = this.terrainPass.waterLevel;
    this.u32[10] = Math.min(this.boats.length, MAX_BOATS);
    f[11] = 0; // pad

    let off = HEADER_FLOATS;
    const n = this.u32[10]!;
    for (let i = 0; i < n; i++) {
      const b = this.boats[i]!;
      // Stern position: emit a few metres BEHIND the actual stern, so the
      // local Gaussian dome (hardest part of the ripple to control) sits in
      // open water and not under the boat hull.
      const ch = Math.cos(b.heading);
      const sh = Math.sin(b.heading);
      const fwdX = sh;
      const fwdZ = -ch;
      const sternOffset = b.params.length * 0.55 + 2.5;
      const sternX = b.posX - fwdX * sternOffset;
      const sternZ = b.posZ - fwdZ * sternOffset;
      const speed = Math.hypot(b.surge, b.sway);
      // Per-frame impulse on h_t at the stern. Kept small so the local
      // displacement under the boat is at most a few cm; the trail builds
      // through wave propagation, not through dumping a tall dome at the source.
      const amp = Math.max(0, speed - 0.2) * 0.06;
      f[off + 0] = sternX;
      f[off + 1] = sternZ;
      f[off + 2] = speed;
      f[off + 3] = amp;
      off += BOAT_FLOATS;
      this.prevSpeed.set(b, speed);
    }

    this.device.queue.writeBuffer(this.uniform, 0, this.cpu);

    const pass = encoder.beginComputePass({ label: "RippleBufferPass" });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.currentIsB ? this.bgBA : this.bgAB);
    const wg = Math.ceil(this.resolution / 8);
    pass.dispatchWorkgroups(wg, wg, 1);
    pass.end();
    this.currentIsB = !this.currentIsB;
  }

  destroy(): void {
    this.texA.destroy();
    this.texB.destroy();
  }
}
